/*
  Rinolux cart drawer: reservation timer, animated shipping bar, upsell and second item add,
  discount codes and the checkout button spinner. Everything updates through the Section Rendering API.
*/
(() => {
  if (window.__pfCart) return;
  window.__pfCart = true;

  const SECTIONS = 'cart-drawer,cart-icon-bubble';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const getDrawer = () => document.querySelector('cart-drawer');

  const cartRequest = async (path, body) => {
    const response = await fetch(`${path}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTIONS, sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw data;
    return data;
  };

  const render = (data) => {
    const drawer = getDrawer();
    if (!drawer || !data.sections) return;
    drawer.renderContents(data);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rl-cart', cartData: data });
    }
  };

  /* Reservation timer, continuous across re-renders and page loads in the same session */
  class PfCartTimer extends HTMLElement {
    connectedCallback() {
      this.duration = Number(this.dataset.duration || 600) * 1000;
      this.output = this.querySelector('[data-timer]');
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    endTime() {
      let end = 0;
      try {
        end = Number(sessionStorage.getItem('rl-cart-timer-end')) || 0;
      } catch (e) {}
      if (end < Date.now()) {
        end = Date.now() + this.duration;
        try {
          sessionStorage.setItem('rl-cart-timer-end', String(end));
        } catch (e) {}
      }
      return end;
    }

    tick() {
      const left = Math.max(0, Math.round((this.endTime() - Date.now()) / 1000));
      const minutes = String(Math.floor(left / 60)).padStart(2, '0');
      const seconds = String(left % 60).padStart(2, '0');
      if (this.output) this.output.textContent = `${minutes}:${seconds}`;
    }
  }
  if (!customElements.get('rl-cart-timer')) customElements.define('rl-cart-timer', PfCartTimer);

  /* Shipping bar. Grows from the last shown width to the new one */
  class PfProgressBar extends HTMLElement {
    connectedCallback() {
      const fill = this.querySelector('.rl-cart-progress__fill');
      if (!fill) return;
      const next = Math.min(Math.max(Number(this.dataset.progress) || 0, 0), 100);
      const previous = typeof window.__pfProgress === 'number' ? window.__pfProgress : next;
      window.__pfProgress = next;
      if (reduceMotion.matches || previous === next) return;
      fill.style.transition = 'none';
      fill.style.width = `${previous}%`;
      fill.getBoundingClientRect();
      requestAnimationFrame(() => {
        fill.style.transition = '';
        fill.style.width = `${next}%`;
      });
    }
  }
  if (!customElements.get('rl-progress-bar')) customElements.define('rl-progress-bar', PfProgressBar);

  /* Discount codes */
  class PfDiscountForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.error = this.querySelector('.rl-discount__error');
      this.codes = (this.dataset.codes || '').split(',').filter(Boolean);
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply(this.form.discount.value.trim());
      });
      this.querySelectorAll('[data-remove-code]').forEach((button) =>
        button.addEventListener('click', () => this.remove(button.dataset.removeCode))
      );
    }

    setBusy(busy) {
      this.toggleAttribute('aria-busy', busy);
      this.querySelectorAll('button, input').forEach((el) => (el.disabled = busy));
    }

    showError() {
      if (!this.error) return;
      this.error.textContent = this.dataset.error;
      this.error.hidden = false;
    }

    async apply(code) {
      if (!code) return;
      if (this.codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) return;
      this.setBusy(true);
      try {
        const data = await cartRequest(window.routes.cart_update_url, { discount: [...this.codes, code].join(',') });
        const applied = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
        if (!applied || !applied.applicable) {
          // Keep the cart clean: drop the code that did not apply
          const clean = await cartRequest(window.routes.cart_update_url, { discount: this.codes.join(',') });
          render(clean);
          requestAnimationFrame(() => getDrawer()?.querySelector('rl-discount-form')?.showError());
          return;
        }
        render(data);
      } catch (e) {
        this.setBusy(false);
        this.showError();
      }
    }

    async remove(code) {
      this.setBusy(true);
      try {
        const rest = this.codes.filter((existing) => existing !== code);
        render(await cartRequest(window.routes.cart_update_url, { discount: rest.join(',') }));
      } catch (e) {
        this.setBusy(false);
      }
    }
  }
  if (!customElements.get('rl-discount-form')) customElements.define('rl-discount-form', PfDiscountForm);

  /* Upsell add */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-rl-add]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    try {
      const data = await cartRequest(window.routes.cart_add_url, {
        items: [{ id: Number(button.dataset.rlAdd), quantity: 1 }],
      });
      render(data);
    } catch (e) {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  });

  /* Second item offer: the picked variant is the one the button adds. Stopped before it reaches
     Dawn's cart items, which would read the change as a quantity update */
  document.addEventListener(
    'change',
    (event) => {
      const picker = event.target.closest('[data-rl-second-picker]');
      if (!picker) return;
      event.stopPropagation();
      const card = picker.closest('.rl-second');
      const button = card.querySelector('[data-rl-add]');
      if (button) button.dataset.rlAdd = picker.value;
      const image = card.querySelector('[data-rl-second-image]');
      const src = picker.selectedOptions[0]?.dataset.image;
      if (image && src) image.src = src;
    },
    true
  );

  /* Checkout buttons show a spinner while the checkout loads */
  document.addEventListener('submit', (event) => {
    const button = event.submitter;
    if (!button || button.name !== 'checkout' || event.defaultPrevented) return;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('button[name="checkout"].loading').forEach((button) => {
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    });
  });
})();

/* Cart upsell cards: the option selects pick the variant that the card adds */
document.addEventListener('change', (event) => {
  const picker = event.target.closest('[data-rl-upsell-picker]');
  if (!picker) return;
  const card = picker.closest('.rl-upsells__item');
  const variants = JSON.parse(picker.querySelector('script[type="application/json"]').textContent);
  const chosen = [...picker.querySelectorAll('select')].map((s) => s.value);
  const match = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
  const input = card.querySelector('input[name="id"]');
  const button = card.querySelector('.upsell__add-btn');
  if (match && input) input.value = match.id;
  if (button) button.disabled = !match || !match.available;
});
